import React from "react";
import classNames from "classnames";

import { blankStyleOverrides, stylingOverrideReturns } from "../../utils";
import { ContentEditors, CreateVideo, LanguagesAbbr, Session, SessionTypesEnum, VideoProgressData } from "../../../../types/working-model";
import { TypographyItem } from "../../../general-ui/typography-item/typography-item";
import { niceDateTimeRange } from "../../../../utils/utils";
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useTranslate } from "../../../../i18n/useTranslationModules";
import { OptionalComponent } from "../../../../utils/optional-component";
import { ParamsProps } from "../../live-event";
import { useHistory, useParams } from "react-router";
import { isArray } from "underscore";
import { useScreenMediaQuery } from "../../../../utils/use-screen-media-query";
import { EditorSizes, TemplateNames, TextAlignmentsEnum } from "../../../../types/template-layouts";
import { Button } from "../../../general-ui/button/button";
import handleSessionCardClick from "../../../../utils/handle-session-card-click";
import RenderEditorDescription from "../../../general-ui/editable-text/render-editor-description";


interface CalendarOndemandSessionCardProps {
	session: Session;
	stylingOverrides?: stylingOverrideReturns;
	template: string;
	defaultImage: string;
	selectedLanguage?: LanguagesAbbr;
	editorSize?: 'desktop' | 'tablet' | 'mobile';
	isLandingPage?: boolean;
	isCalendarLayout?: boolean;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
}

const CalendarOndemandSessionCard: React.FC<CalendarOndemandSessionCardProps> = ({
	session,
	stylingOverrides = blankStyleOverrides,
	defaultImage,
	selectedLanguage,
	isCalendarLayout = false
}) => {
	const finishedRegistering = useTypedSelector(state => state.LiveEventReducer.finishedRegistering);
	const userVerified = useTypedSelector(state => state.LiveEventReducer.userVerified);
	const firesidesHostSessions = useTypedSelector(state => state.LiveEventReducer.firesidesHostSessions);
	const validSessions = useTypedSelector(state => state.LiveEventReducer.validSessions);
	const paidSessions = useTypedSelector(state => state.LiveEventReducer.paidSessions);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;

	const { isLessThan640 } = useScreenMediaQuery();
	const { language, eventName, uuid }: ParamsProps = useParams();
	const history = useHistory();
	const { t } = useTranslate(['landing', 'homepage']);

	const video = isArray(session.on_demand_video) ? session.on_demand_video[0] : null;
	const progressDataString = video ? localStorage.getItem(`video-played-${session.session}-${video.source}`) : null;
	const event = eventBundle ?? workingEvent;
	const isMobile = eventBundle ? isLessThan640 : editorSize === EditorSizes.mobile;
	const sessionCardDescription = t(`agenda.session.${session.session}.description`, session.description?.[language] as string || session.description?.base || '');

	const template = event?.template.name || TemplateNames.Classic;

	const {
		secondaryButtonOverrides,
		moduleTitleOverrides,
		moduleDescriptionOverrides,
		descriptionOverrides } = stylingOverrides;


	const getProgressPercent = () => {
		if (session && session.session_type === SessionTypesEnum.onDemand) {
			if (!video?.source) return;
			// get the video left-off data from localStorage to set the progress bar percent with
			const progressData: VideoProgressData | null = progressDataString ? JSON.parse(progressDataString) : null;
			if (progressData && progressData.duration && progressData.duration !== 0) {
				const progPercent = (progressData.playedSeconds / progressData.duration) * 100;
				return Math.round(progPercent);
			}
		}
		return 0;
	};

	const handleSessionClick = () => {
		handleSessionCardClick({
			event,
			workingEvent,
			session,
			language,
			eventName,
			validSessions,
			finishedRegistering,
			userVerified,
			firesidesHostSessions,
			paidSessions,
			paramsUuid: uuid,
			history,
			selectedLanguage,
		});
	};

	const disableSessionThumbnails = workingEvent?.settings?.disable_session_thumbnails;

	const renderCalendarListViewSessionCard = (): JSX.Element => {
		// Sessions can come in as Sessions or Session Previews
		const actualSession = event?.sessions
			? event?.sessions?.find(s => s.session === session.session)
			: event?.sessionsPreviews?.find(s => s.session === session.session);

		if (!actualSession) return <></>;

		const actualSessionAsSession = actualSession as Session;

		const formattedTime = () => {

			// Try to get the broadcast start time first
			let timestamp = actualSession.timestamp;

			if (!actualSessionAsSession.on_demand_video && !timestamp) return '';

			// If that doesn't exist, see if there's an on demand video created date
			if (!timestamp && actualSession.session_type === SessionTypesEnum.onDemand) {
				const onDemandVideo = Array.isArray(actualSessionAsSession.on_demand_video)
					? actualSessionAsSession.on_demand_video[0]
					: actualSessionAsSession.on_demand_video;
				const date = (onDemandVideo as any)?.created_at as string | undefined;
				if (date) timestamp = new Date(date).valueOf();
			}

			if (!timestamp) return ('');
			const timeText = isMobile ? niceDateTimeRange(timestamp, undefined, language)
				: niceDateTimeRange(timestamp, actualSession.end_timestamp ?? undefined, language);

			return (timeText);
		};

		const duration = () => {
			if (!actualSessionAsSession.on_demand_video) return '';
			const odVids = actualSessionAsSession.on_demand_video as CreateVideo[];
			let timeString = '';
			if (!odVids || !odVids.length) {
				return timeString;
			}

			const dur = odVids[0]?.duration ? odVids[0]?.duration : (odVids[0]?.recording_duration_ms ?? 0) / 1000 ?? 0;

			const hours = Math.floor(dur / 3600);
			const remainingSeconds = dur % 3600;
			const minutes = Math.floor(remainingSeconds / 60);
			const seconds = Math.round(remainingSeconds % 60).toLocaleString('en-US', {
				minimumIntegerDigits: 2,
				useGrouping: false
			});

			if (hours > 0) {
				timeString = `${hours}H ${minutes}M`;
			}
			else {
				timeString = `${minutes}M ${seconds}S`;
			}

			return timeString;
		};

		const timeDateString = (): JSX.Element => {
			const dur = duration();
			const time = formattedTime();
			return (
				<>{dur} {dur && time ? '•' : ''} {time}</>
			);
		};

		return (
			<div className="ondemand-carousel-card" onClick={handleSessionClick}>
				<OptionalComponent display={!disableSessionThumbnails || isCalendarLayout}>
					<div className="image-container">
						{session.image ?
							<img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />
						}
					</div>

				</OptionalComponent>

				<TypographyItem
					className={classNames('ondemand-carousel-card-title-content evt-typography-h5', moduleTitleOverrides)}
					tagName="h3"
				>
					{t(`agenda.session.${session.session}.title`, session.title[language] as string || session.title.base || '')}
				</TypographyItem>
				{session?.on_demand_video && progressDataString && <div
					className={classNames('ondemand-carousel-card-video-progress-background')}
				>
					<div
						style={{ width: `${getProgressPercent()}%` }}
						className={classNames('ondemand-carousel-card-video-progress-indicator')}
					>
					</div>
				</div>}

				<div className="hover-card-details">
					<div className="hover-card-details-top">
						<h6 className="date-time"> {timeDateString()}</h6>
						<TypographyItem
							className={classNames('ondemand-carousel-card-title evt-typography-h5 text-ellipsis', moduleTitleOverrides)}
							tagName="h3"
						>
							{t(`agenda.session.${session.session}.title`, session.title[language] as string || session.title.base || '')}
						</TypographyItem>

						<TypographyItem className={classNames('agenda-session-card-description evt-typography-subtitle-b4', moduleDescriptionOverrides)}>
							<RenderEditorDescription
								description={sessionCardDescription}
								descriptionOverrides={classNames(descriptionOverrides)}
								useNew={contentEditor === ContentEditors.quill}
								defaultTextAlignment={TextAlignmentsEnum.left}
							/>
						</TypographyItem>
					</div>
					<Button
						classButton={classNames("primary", secondaryButtonOverrides)}
						template={template}
						onClick={() => null}
					>
						{t('homepage:Watch', "Watch")}
					</Button>
				</div>
			</div>
		);
	};

	return (<>
		{renderCalendarListViewSessionCard()}
	</>);
};

export default CalendarOndemandSessionCard;